<template>
  <div>
    <div class="d-flex">
      <v-chip label>{{ voter }}</v-chip>
      <v-spacer></v-spacer>
      <div v-if="voter" class="text-right d-flex">
        <v-btn depressed text color="red" to="/">
          Logout
          <v-icon right>mdi-logout-variant</v-icon>
        </v-btn>
      </div>
    </div>

    <h1 class="display-1 mt-5 mb-6">
      {{ election.association.description }} Election
    </h1>

    <v-alert
      border="right"
      colored-border
      type="warning"
      elevation="2"
      dense
      icon="mdi-alert-rhombus"
      class="mb-4"
    >
      Do not close or refresh this page
    </v-alert>

    <section>
      <div v-for="(position, i) in positions" :key="position.id">
        <v-list
          dense
          shaped
          :color="`${getColor(i % 2)} lighten-5`"
          class="px-2 mb-5"
        >
          <h3 class="mt-3 px-4">{{ position.description }} POSITION</h3>

          <v-subheader class="mx-2"> Click a candidate to select </v-subheader>

          <v-list-item-group
            color="secondary"
            v-if="position.candidates.length > 0"
          >
            <v-list-item
              class="pa-3 pb-4"
              :key="candidate.id"
              :value="candidate.id"
              v-for="candidate in position.candidates"
              :active-class="`${getColor(
                i % 2
              )} accent-2 grey--text text--darken-3`"
            >
              <template v-slot:default="{ active }">
                <v-row
                  class="d-flex px-4"
                  @click="selectCandidate(!active, position.id, candidate.id)"
                >
                  <v-list-item-icon class="mr-2">
                    <v-img
                      top
                      cover
                      class="candidate-image"
                      :src="`${
                        candidate.profile.photo
                          ? `${candidate.profile.photo}`
                          : '/favicon.png'
                      }`"
                      transition="scale-transition"
                      width="50"
                      height="50"
                    >
                    </v-img>
                  </v-list-item-icon>

                  <v-list-item-content class="mt-2">
                    <v-list-item-title>
                      <h3 class="mb-1">{{ candidate.profile.name }}</h3>
                    </v-list-item-title>
                    <v-list-item-subtitle
                      class="text-uppercase font-weight-regular text-caption"
                    >
                      {{ candidate.profile.reg_number }}
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-checkbox
                      color="primary"
                      :input-value="active"
                    ></v-checkbox>
                  </v-list-item-action>
                </v-row>
              </template>
            </v-list-item>
          </v-list-item-group>

          <h3 v-else class="my-2 mx-4 red--text">
            No candidate for this position
          </h3>
        </v-list>
      </div>
    </section>
    <v-divider class="my-5"></v-divider>
    <section class="mb-7">
      <h1>Ballot Summary</h1>
      <p>
        You have voted for {{ ballot.length }} positions out of
        {{ positions.length }}
      </p>
      <div class="mt-2">
        <v-form class="mt-5" :disabled="casting" v-model="phoneFormValid">
          <v-text-field
            v-model="phone"
            prepend-inner-icon="mdi-phone-outline"
            clearable
            outlined
            :rules="rules.phone"
            v-mask="'NNNN-NNN-NNNN'"
            label="Your phone number"
            placeholder="Enter the phone number you supplied during accreditation to cast your vote"
          ></v-text-field>
        </v-form>

        <v-btn
          x-large
          color="green"
          @click="castVotes"
          :disabled="ballot.length == 0 || !phoneFormValid || casting"
          :loading="casting"
        >
          Cast {{ ballot.length > 1 ? "votes" : "vote" }}
          <v-icon right>mdi-vote</v-icon>
        </v-btn>
      </div>
    </section>

    <Auth
      :uuid="uuid"
      :open="!authenticated"
      :title="election.association.description"
      :session="election.session.sSesName"
      @success="receiveCredentials"
    />
  </div>
</template>

<script>
import Auth from "./Auth";
import { phone } from "../../tools/rules";
import { formatDate } from "../../tools/format";
export default {
  props: ["uuid"],

  components: { Auth },

  data: () => ({
    authenticated: false,
    credentials: {},
    election: { association: { description: null } },
    ballot: [],
    positions: [],
    voter: null,
    sid: null,
    electionFound: false,
    positionsFound: false,
    casting: false,

    rules: {
      phone,
    },
    phone: null,
    phoneFormValid: false,
  }),

  methods: {
    formatDate,
    getColor(i) {
      switch (i) {
        case 0:
          return "green";
        default:
          return "lime";
      }
    },
    receiveCredentials(credentials, voter, sid) {
      this.sid = sid;
      this.voter = voter;
      this.authenticated = !!voter;
      this.credentials = credentials;

      this.loadElectionPositions(sid);
    },
    loadElectionDetails() {
      this.electionFound = false;

      this.$axios
        .get(`/details/${this.uuid}`)
        .then(({ data: election }) => {
          this.election = election;
          this.electionFound = true;
        })
        .catch((error) => {
          if (error.response.status == 404)
            this.$router.push({ name: "elections" });

          this.$bus.$emit("snackbar", {
            type: "error",
            message: "Election not found",
          });
        });
    },
    loadElectionPositions(sid) {
      this.positionsFound = false;

      this.$axios
        .get(`/positions/${this.uuid}/${sid}`)
        .then(({ data: positions }) => {
          this.positions = positions;
          this.positionsFound = true;
        })
        .catch((error) => {
          if (error.response.status == 404)
            this.$router.push({ name: "elections" });

          this.$bus.$emit("snackbar", {
            type: "error",
            message: "Could not load election positions",
          });
        });
    },
    selectCandidate(selected, position, candidate) {
      const cast = { position, candidate };
      this.ballot = [...this.ballot].filter((ba) => ba.position != position);
      if (selected) this.ballot.push(cast);
    },
    castVotes() {
      const data = {
        phone: this.phone,
        ballot: this.ballot,
        credentials: this.credentials,
      };

      this.casting = true;

      this.$axios({
        url: `/cast-vote-pg/${this.uuid}`,
        method: "POST",
        data,
      })
        .then(() => {
          this.$router.push({ name: "live" });

          this.$bus.$emit("snackbar", {
            type: "success",
            message: "Your votes has been cast",
          });
        })
        .catch(() => {
          this.$bus.$emit("snackbar", {
            type: "error",
            message: "Vote casting failed",
          });
        })
        .finally(() => {
          this.casting = false;
        });
    },
  },

  mounted() {
    this.loadElectionDetails();
  },
};
</script>

<style>
</style>
